import React, { createContext, useContext, useState, useCallback } from "react";
import {
  getEnrollments,
  approveEnrollment,
  rejectEnrollment,
  deleteEnrollment,
  Enrollment,
} from "../services/enrollmentService";
import { useNotification } from "./NotificationContext";

interface EnrollmentContextType {
  enrollments: Enrollment[];
  loading: boolean;
  error: string | null;
  fetchEnrollments: () => Promise<void>;
  approveEnrollment: (id: string) => Promise<void>;
  rejectEnrollment: (id: string) => Promise<void>;
  deleteEnrollment: (id: string) => Promise<void>;
}

const EnrollmentContext = createContext<EnrollmentContextType | undefined>(
  undefined
);

export const useEnrollment = () => {
  const context = useContext(EnrollmentContext);
  if (!context) {
    throw new Error("useEnrollment must be used within an EnrollmentProvider");
  }
  return context;
};

export const EnrollmentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const fetchEnrollments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getEnrollments();
      setEnrollments(data);
    } catch (err) {
      setError("Failed to fetch enrollments");
      showError("Failed to fetch enrollments");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleApproveEnrollment = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const updatedEnrollment = await approveEnrollment(id);
      setEnrollments((prev) =>
        prev.map((enrollment) =>
          enrollment._id === id ? updatedEnrollment : enrollment
        )
      );
      showSuccess("Enrollment approved successfully");
    } catch (err) {
      setError("Failed to approve enrollment");
      showError("Failed to approve enrollment");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectEnrollment = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const updatedEnrollment = await rejectEnrollment(id);
      setEnrollments((prev) =>
        prev.map((enrollment) =>
          enrollment._id === id ? updatedEnrollment : enrollment
        )
      );
      showSuccess("Enrollment rejected successfully");
    } catch (err) {
      setError("Failed to reject enrollment");
      showError("Failed to reject enrollment");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnrollment = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await deleteEnrollment(id);
      setEnrollments((prev) =>
        prev.filter((enrollment) => enrollment._id !== id)
      );
      showSuccess("Enrollment deleted successfully");
    } catch (err) {
      setError("Failed to delete enrollment");
      showError("Failed to delete enrollment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <EnrollmentContext.Provider
      value={{
        enrollments,
        loading,
        error,
        fetchEnrollments,
        approveEnrollment: handleApproveEnrollment,
        rejectEnrollment: handleRejectEnrollment,
        deleteEnrollment: handleDeleteEnrollment,
      }}
    >
      {children}
    </EnrollmentContext.Provider>
  );
};
